import { createContext, useContext, useMemo, ReactNode } from 'react';
import { FeatureFlags } from '@/types/feature-flags.types';
import { useAuth } from '@/contexts/AuthContext';
import { useSystemSettings } from '@/api/hooks/useEntityConfig';

// Deployment defaults — the build-time env values. These remain the complete
// fallback: tenants without a stored override (entity_configuration.
// featuresjson NULL) behave byte-for-byte as before the runtime merge.
const defaultFlags: FeatureFlags = {
  requisitions: import.meta.env.VITE_FEATURE_REQUISITIONS === 'true',
  recovery: import.meta.env.VITE_FEATURE_RECOVERY === 'true',
  damage: import.meta.env.VITE_FEATURE_DAMAGE === 'true',
  repair: import.meta.env.VITE_FEATURE_REPAIR === 'true',
  reports: import.meta.env.VITE_FEATURE_REPORTS === 'true',
  clients: import.meta.env.VITE_FEATURE_CLIENTS === 'true',
  trends: import.meta.env.VITE_FEATURE_TRENDS === 'true',
  advancedDashboard: import.meta.env.VITE_FEATURE_ADVANCED_DASHBOARD === 'true',
  userClientAssociation: import.meta.env.VITE_FEATURE_USER_CLIENT_ASSOCIATION === 'true',
  maintenance: import.meta.env.VITE_FEATURE_MAINTENANCE === 'true',
  productionOperations: import.meta.env.VITE_FEATURE_PRODUCTION_OPERATIONS === 'true',
  incidents: import.meta.env.VITE_FEATURE_INCIDENTS !== 'false',
  approvals: import.meta.env.VITE_FEATURE_APPROVALS !== 'false',
  // Change Governance. Default OFF everywhere — enabling it changes governed
  // endpoints from 200 to 202, so it is a deliberate per-tenant opt-in made
  // from Admin -> System Config after a policy has been bound.
  approvalsV2: import.meta.env.VITE_FEATURE_APPROVALS_V2 === 'true',
  // Billing — TOCA-only. Default off; .env.toca sets it true.
  billing: import.meta.env.VITE_FEATURE_BILLING === 'true',
  // Pallet Manufacture — TOCA-only. Default off; .env.toca sets it true.
  palletManufacture: import.meta.env.VITE_FEATURE_PALLET_MANUFACTURE === 'true',
  // Pallet Incidents — TOCA-only. Default off; .env.toca sets it true.
  palletIncidents: import.meta.env.VITE_FEATURE_PALLET_INCIDENTS === 'true',
  // AI ("Pregúntale a ActiveID"). Default ON unless the build disables it —
  // mirrors the backend IAiTenantGate (on when process-enabled + no override).
  // The control plane toggles the per-tenant override (featuresjson["ai"]),
  // which now survives sanitize() because "ai" is a known key.
  ai: import.meta.env.VITE_FEATURE_AI !== 'false',
  // Dynamic Workflows engine pages (shipments, orders, flow designer). Default
  // ON so TOCA/Demo are unchanged; .env.sgs turns it off because SGS's API runs
  // with Workflows:Enabled=false and those endpoints 404. Build-time only — not
  // offered in System Config, since flipping it cannot enable the server side.
  workflows: import.meta.env.VITE_FEATURE_WORKFLOWS !== 'false',
  // ERP Oracle import scaffold — SGS/Asset only. Default off; .env.sgs sets it
  // true. Mirrors the backend, which 404s this endpoint on Pallet tenants.
  erpOracleImport: import.meta.env.VITE_FEATURE_ERP_ORACLE_IMPORT === 'true',
  // Per-tenant login branding (default off). Set via System Configuration →
  // Features; read pre-login by the public-branding endpoint. Known key so it
  // survives sanitize() and round-trips through FeaturesJson.
  loginHideTitle: import.meta.env.VITE_FEATURE_LOGIN_HIDE_TITLE === 'true',
};

/**
 * The set of keys the client recognises. A tenant override may only touch
 * these; any stray/unknown key from the API is ignored so a bad DB row can
 * never inject flags the app doesn't understand.
 */
const KNOWN_FLAG_KEYS = Object.keys(defaultFlags) as (keyof FeatureFlags)[];

/**
 * Keep only recognised keys whose value is strictly boolean. Anything else
 * (null, undefined, strings, unknown keys) is dropped, so the merge below can
 * only ever narrow to `{ ...defaultFlags, ...validOverrides }`.
 */
function sanitize(
  features: Record<string, boolean> | null | undefined
): Partial<FeatureFlags> {
  if (!features || typeof features !== 'object') return {};
  const out: Partial<FeatureFlags> = {};
  for (const key of KNOWN_FLAG_KEYS) {
    const value = (features as Record<string, unknown>)[key];
    if (typeof value === 'boolean') {
      out[key] = value;
    }
  }
  return out;
}

/** Exposed so the admin page can seed its switches from the deployment defaults. */
export function getDefaultFlags(): FeatureFlags {
  return defaultFlags;
}

const FeatureFlagContext = createContext<FeatureFlags>(defaultFlags);

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  // Reuses the sidebar's query key → exactly one system-settings request.
  // Gated on auth so anonymous/login screens keep pure deployment defaults.
  const { data } = useSystemSettings({ enabled: isAuthenticated });

  const flags = useMemo<FeatureFlags>(() => {
    // Unauthenticated, loading, or no tenant override → deployment defaults,
    // byte-for-byte the pre-runtime-merge behaviour.
    if (!isAuthenticated || !data) return defaultFlags;
    return { ...defaultFlags, ...sanitize(data.features) };
  }, [isAuthenticated, data]);

  return (
    <FeatureFlagContext.Provider value={flags}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlags(): FeatureFlags {
  return useContext(FeatureFlagContext);
}

export function useFeature(feature: keyof FeatureFlags): boolean {
  const flags = useFeatureFlags();
  // Optional flags (Phase 6's `dynamicKpis`, future opt-ins) come back as
  // `boolean | undefined`. Coerce to a definite boolean so callers never
  // accidentally render their gated content under `undefined`.
  return !!flags[feature];
}
